import { addDays } from '../../constants/schedule';

/**
 * Prazos de cobrança, e o que é crítico.
 *
 * O laboratório trabalha meses num serviço e o dinheiro entra no fim. Duas
 * coisas fazem esse dinheiro sumir sem ninguém notar:
 *
 *   1. a fatura vence e ninguém cobra;
 *   2. o serviço fica pronto e NINGUÉM FATURA.
 *
 * A segunda é a pior, porque não deixa rastro: não há fatura vencida para
 * aparecer em lista nenhuma. Um serviço concluído e não faturado é silêncio
 * completo até alguém lembrar dele.
 *
 * Por isso as duas viram CRÍTICO, e as duas vão para o calendário — o
 * calendário é onde se olha para saber o que fazer hoje.
 */

/**
 * Dias para pagar, contados da TE.
 *
 * O relógio começa quando os embriões saem — não quando a fatura é emitida.
 * Amarrar à emissão deixaria o prazo à mercê do dia em que alguém lembrou de
 * faturar, e um mês de atraso na emissão viraria um mês a mais de prazo para o
 * cliente.
 *
 * Não é um prazo "da entrada": é o prazo DA TE. Quem tem entrada paga a entrada
 * nele; quem fatura na própria TE — o "por transferência" — paga nele a fatura
 * do serviço. `relogioDaTE` diz qual dos dois é o caso.
 */
export const PRAZO_DESDE_A_TE = 21;

/** Prazo padrão das demais faturas, quando a parcela não traz vencimento. */
export const PRAZO_PADRAO = 30;

const dia = (v) => (v ? String(v).slice(0, 10) : null);
const hojeISO = (hoje = new Date()) => hoje.toISOString().slice(0, 10);

/**
 * Quando o prazo contado da TE cai, neste serviço.
 *
 * Só existe depois da TE lançada: antes disso não há embrião transferido, e
 * portanto não há o que cobrar. Serviço sem data de TE não tem prazo, e isso
 * é diferente de ter um prazo que já passou.
 */
export const vencimentoDesdeATE = (service) => {
  const te = dia(service?.te_date);
  if (!te || !service?.te_done) return null;
  return addDays(te, PRAZO_DESDE_A_TE);
};

/**
 * Os prazos de pagamento de um serviço, para o calendário.
 *
 * Duas fontes, e elas não se repetem:
 *   - a entrada, derivada da TE, some assim que a fatura de entrada é paga;
 *   - as parcelas emitidas, cada uma com seu `vencimento`.
 *
 * Parcela paga não vira evento. O calendário responde "o que precisa de ação",
 * e recibo não precisa.
 */
export const prazosDoServico = (
  service, parcelas = [], entradaPaga = 0,
  // O QUE vence nos 21 dias da TE, e quanto. Vem de fora pelo mesmo motivo do
  // `valor` de `criticidadeDoServico`: decidir aqui obrigaria este arquivo a
  // conhecer os sete modelos de cobrança.
  { relogio = 'entrada', valor = 0 } = {},
) => {
  const eventos = [];

  /**
   * O prazo DERIVADO da entrada só vale enquanto ela não virou fatura.
   *
   * Depois de emitida, a parcela dela entra nesta mesma lista com vencimento
   * próprio, e o laço abaixo já a trata — inclusive a quitação, que passa a
   * ser a da parcela e não o palpite "entrou alguma coisa".
   *
   * Duas coisas se consertam aqui. A primeira é a duplicata: sem esta
   * condição, a entrada emitida apareceria duas vezes no calendário, uma pelo
   * prazo derivado e outra pela parcela. A segunda é o atalho antigo: o teste
   * era `entradaPaga <= 0`, então UM real recebido apagava o prazo dos
   * setecentos e cinquenta e nove restantes.
   */
  const entradaJaFaturada = (parcelas || []).some(p => p?.ehEntrada);
  const venc = vencimentoDesdeATE(service);
  if (venc && relogio === 'entrada' && !entradaJaFaturada && Number(entradaPaga) <= 0) {
    eventos.push({ tipo: 'entrada', data: venc, rotulo: 'Prazo da entrada' });
  }

  /**
   * Quem fatura NA TE não tem entrada, e tem prazo.
   *
   * São dez serviços em produção, a maioria do laboratório. Não havendo
   * adiantamento a cobrar, os 21 dias passam a valer para a fatura do próprio
   * serviço — e o valor que vence é o do serviço, não de um documento à parte.
   *
   * Vale só ENQUANTO a fatura não saiu: emitida, a parcela abaixo traz o
   * vencimento de verdade, que pode ter sido editado na emissão. Sem esta
   * condição o mesmo dinheiro apareceria duas vezes no calendário.
   */
  if (venc && relogio === 'pagamento' && !service?.billed) {
    eventos.push({
      tipo: 'pagamento', data: venc, rotulo: 'Prazo de pagamento',
      valor: Number(valor) || 0,
    });
  }

  (parcelas || []).forEach((p) => {
    if (p?.paga_em) return;
    const v = dia(p?.vencimento);
    if (!v) return;
    eventos.push({
      tipo: 'parcela',
      data: v,
      rotulo: p.total > 1 ? `Parcela ${p.numero}/${p.total}` : 'Vencimento',
      valor: Number(p.valor) || 0,
    });
  });

  return eventos.sort((a, b) => a.data.localeCompare(b.data));
};

/**
 * O serviço pode ser faturado e ainda não foi?
 *
 * `valor > 0` é quem responde "pode": ele já embute o modelo de cobrança, as
 * etapas exigidas e o `sem_cobranca`. Um serviço sem valor calculado não está
 * esperando fatura — está esperando etapa.
 */
export const faturavelNaoFaturado = (service, valor) =>
  !service?.billed && Number(valor) > 0;

/**
 * Por que este serviço é crítico — e, se não for, por que não.
 *
 * Devolve os motivos em lista, e não um booleano, porque a tela precisa dizer
 * QUAL é o problema. "Crítico" sozinho manda a pessoa procurar.
 *
 * `valor` vem de fora (`calculateServiceValue`) para esta função não depender
 * dos sete modelos de cobrança — e para poder ser testada sem eles. Pela mesma
 * razão `entradaAEmitir` chega pronta: quem a calcula precisa do laboratório,
 * que é justamente o que esta função não quer conhecer.
 */
export const criticidadeDoServico = (
  service,
  { parcelas = [], valor = 0, entradaPaga = 0,
    entradaEmitida = false, entradaAEmitir = null } = {},
  hoje = new Date(),
) => {
  const h = hojeISO(hoje);
  const motivos = [];

  // 1. Parcela emitida que venceu e não foi paga.
  const vencidas = (parcelas || []).filter(p => !p?.paga_em && dia(p?.vencimento) && dia(p.vencimento) < h);
  if (vencidas.length > 0) {
    const maisVelha = vencidas.map(p => dia(p.vencimento)).sort()[0];
    motivos.push({
      tipo: 'parcela_vencida',
      desde: maisVelha,
      dias: diasEntre(maisVelha, h),
      texto: vencidas.length === 1
        ? `Parcela vencida há ${diasEntre(maisVelha, h)}d`
        : `${vencidas.length} parcelas vencidas, a mais antiga há ${diasEntre(maisVelha, h)}d`,
    });
  }

  // 2. A entrada que nunca foi cobrada.
  //
  // Vem ANTES da vencida porque é outra conversa, com outra pessoa: aqui quem
  // deve agir é o laboratório, não o cliente. Enquanto só existia
  // `entrada_vencida`, a tela dizia "Entrada vencida há 3d" de uma fatura que
  // nunca tinha saído — acusava o cliente de atrasar uma cobrança que ele
  // nunca recebeu, e mandava a pessoa cobrar em vez de emitir.
  //
  // É crítico desde o primeiro dia, como o `nao_faturado`, e pela mesma razão:
  // dinheiro que ninguém pediu não deixa rastro nenhum. Não há fatura vencida
  // para aparecer em lista, não há cliente reclamando. É silêncio até alguém
  // lembrar.
  const teDia = dia(service?.te_date);
  if (entradaAEmitir?.deve) {
    motivos.push({
      tipo: 'entrada_a_emitir',
      desde: teDia,
      dias: teDia ? diasEntre(teDia, h) : 0,
      valor: Number(entradaAEmitir.valor) || 0,
      texto: teDia
        ? `Entrada a emitir, TE há ${diasEntre(teDia, h)}d`
        : 'Entrada a emitir',
    });
  }

  // 3. A entrada EMITIDA passou dos 21 dias da TE sem nada receber.
  //
  // `entradaEmitida` é o que separa este motivo do de cima. O prazo continua
  // contado da TE, e não da emissão — amarrá-lo à emissão deixaria o prazo à
  // mercê do dia em que alguém lembrou de faturar.
  const venc = vencimentoDesdeATE(service);
  if (entradaEmitida && venc && Number(entradaPaga) <= 0 && venc < h && !service?.paid) {
    motivos.push({
      tipo: 'entrada_vencida',
      desde: venc,
      dias: diasEntre(venc, h),
      texto: `Entrada vencida há ${diasEntre(venc, h)}d`,
    });
  }

  // 4. Pronto para faturar e não faturado. O que não deixa rastro.
  if (faturavelNaoFaturado(service, valor)) {
    motivos.push({ tipo: 'nao_faturado', texto: 'Pode faturar e não foi faturado' });
  }

  return { critico: motivos.length > 0, motivos };
};

/** Dias inteiros entre duas datas ISO, sempre positivo. */
export const diasEntre = (de, ate) => {
  if (!de || !ate) return 0;
  const ms = Date.parse(`${dia(ate)}T00:00:00Z`) - Date.parse(`${dia(de)}T00:00:00Z`);
  return Math.max(0, Math.round(ms / 86400000));
};

